import { isNotNil } from "client/Misc/utils";
import { IValidationRule } from "./rules";
import {
	AdditionalValidator,
	CollectionItemValidator,
	ConditionalValidator,
	IValidator,
	NodeValidator,
	PropertyValidator,
	UniqueKeyGenerator,
	ValidatorExecutionScope
} from "./validator";
import { NodeValidationState } from "./state";
import { ExecuteValidationParams } from "./validationRunner";
import { ModelPath } from "./path";
import { ConversionError } from "client/Forms/converters";

export type IValidationConfiguration<TModel> = IObjectValidatorBuilder<TModel>;

export function createValidationConfiguration<TModel>(): IValidationConfiguration<TModel> {
	return new ObjectValidatorBuilder<TModel>();
}

export const ROOT_NODE_PATH = "Root";

export interface IValidatorBuilder<TModel> {
	add(...validationRules: ReadonlyArray<IValidationRule<TModel>>): this;
	createNode(): IValidator<TModel>;
}

export interface ICollectionValidatorBuilder<
	TCollectionItem,
	TCollection extends ReadonlyArray<TCollectionItem>
>
	extends IValidatorBuilder<TCollection> {
	forItems(
		keyGenerator?: UniqueKeyGenerator<TCollectionItem>
	): IObjectValidatorBuilder<TCollectionItem>;
	forItems(
		externalBuilder: IObjectValidatorBuilder<TCollectionItem>,
		keyGenerator?: UniqueKeyGenerator<TCollectionItem>
	): IObjectValidatorBuilder<TCollectionItem>;
	createNode(): IValidator<TCollection>;
	clone(): ICollectionValidatorBuilder<TCollectionItem, TCollection>;
	addValidators(
		...validators: readonly IObjectValidatorBuilder<TCollectionItem>[]
	): CollectionValidatorBuilder<TCollectionItem, TCollection>;
}

type ICollectionValidatorBuilderOptional<TCollection> = TCollection extends ReadonlyArray<infer R>
	? ICollectionValidatorBuilder<R, TCollection>
	: never;

export interface IObjectValidatorBuilder<TModel> extends IValidatorBuilder<TModel> {
	forScalar<TProperty extends StringKeyOf<TModel>>(property: TProperty): IValidatorBuilder<TModel[TProperty]>;
	forObject<TProperty extends StringKeyOf<TModel>>(property: TProperty): IObjectValidatorBuilder<TModel[TProperty]>;
	forObject<TProperty extends StringKeyOf<TModel>>(
		property: TProperty,
		externalBuilder: IObjectValidatorBuilder<TModel[TProperty]>
	): IObjectValidatorBuilder<TModel[TProperty]>;

	forCollection<TProperty extends StringKeyOf<TModel>>(property: TProperty): ICollectionValidatorBuilderOptional<TModel[TProperty]>;
	addValidators(...validators: readonly IObjectValidatorBuilder<TModel>[]): IObjectValidatorBuilder<TModel>;

	when(
		predicate: (model: TModel) => boolean
	): IObjectValidatorBuilder<TModel>;
	when(
		predicate: (model: TModel) => boolean,
		trueBuilder: IObjectValidatorBuilder<TModel>
	): IObjectValidatorBuilder<TModel>;
	when(
		predicate: (model: TModel) => boolean,
		trueBuilder: IObjectValidatorBuilder<TModel>,
		falseBuilder: IObjectValidatorBuilder<TModel>
	): void;

	whenType<TConditionalModel extends TModel>(
		typeGuardPredicate: (model: TModel) => model is TConditionalModel
	): IObjectValidatorBuilder<TConditionalModel>;
	whenType<TConditionalModel extends TModel>(
		typeGuardPredicate: (model: TModel) => model is TConditionalModel,
		conditionalBuilder: IObjectValidatorBuilder<TConditionalModel>
	): IObjectValidatorBuilder<TConditionalModel>;

	createNode(): IValidator<TModel>;
	clone(): IObjectValidatorBuilder<TModel>;
}

class CollectionValidatorBuilder<
	TCollectionItem,
	TCollection extends ReadonlyArray<TCollectionItem>
> implements ICollectionValidatorBuilder<TCollectionItem, TCollection> {
	private readonly _directRules: IValidationRule<TCollection>[];
	private _childrenBuilder: IObjectValidatorBuilder<TCollectionItem> | undefined;
	private _keyGenerator: UniqueKeyGenerator<TCollectionItem> | undefined;
	private readonly _additionalBuilders: IObjectValidatorBuilder<TCollectionItem>[];
	private _resultNodeValidator: InitializableValidator<TCollection> | undefined;

	constructor(
		another?: CollectionValidatorBuilder<TCollectionItem, TCollection>
	) {
		if (another) {
			this._directRules = [...another._directRules];
			this._childrenBuilder = another._childrenBuilder?.clone();
			this._additionalBuilders = another._additionalBuilders.map(
				x => x.clone()
			);
			this._keyGenerator = another._keyGenerator;
		} else {
			this._directRules = [];
			this._additionalBuilders = [];
		}
	}

	public forItems(
		externalBuilderOrKeyGenerator?: IObjectValidatorBuilder<TCollectionItem> | UniqueKeyGenerator<TCollectionItem>,
		keyGenerator?: UniqueKeyGenerator<TCollectionItem>
	)
		: IObjectValidatorBuilder<TCollectionItem> {

		if (isNotNil(this._resultNodeValidator)) {
			throw new Error("Cannot continue building the validation node, because result was already created.");
		}

		let externalBuilder: IObjectValidatorBuilder<TCollectionItem> | undefined;
		if (typeof externalBuilderOrKeyGenerator === "function") {
			this._keyGenerator = externalBuilderOrKeyGenerator;
			externalBuilder = undefined;
		} else {
			this._keyGenerator = keyGenerator;
			externalBuilder = externalBuilderOrKeyGenerator;
		}

		if (externalBuilder) {
			if (this._childrenBuilder) {
				throw new Error("Cannot register external builder for items collection because some inline rules are already defined for it");
			}

			this._childrenBuilder = externalBuilder;
			return this._childrenBuilder;
		}

		if (!this._childrenBuilder) {
			this._childrenBuilder = new ObjectValidatorBuilder<TCollectionItem>();
		}
		return this._childrenBuilder;
	}

	public add(...validationRules: ReadonlyArray<IValidationRule<TCollection>>): this {
		if (isNotNil(this._resultNodeValidator)) {
			throw new Error("Cannot continue building the validation node, because result was already created.");
		}

		this._directRules.push(...validationRules);
		return this;
	}

	public createNode(): IValidator<TCollection> {
		if (this._resultNodeValidator) {
			return this._resultNodeValidator;
		}

		this._resultNodeValidator = new InitializableValidator<TCollection>(
			(): NodeValidator<TCollection> => {
				const result = new NodeValidator<TCollection>(
					this._directRules,
					[
						...(isNotNil(this._childrenBuilder)
							? [
								new CollectionItemValidator(
									this._childrenBuilder.createNode(),
									this._keyGenerator
								),

							]
							: []
						),
						...this._additionalBuilders.map(
							(x, i) => new CollectionItemValidator(
								new AdditionalValidator(x.createNode(), i)
							)
						)
					]
				);

				return result;
			}
		);

		this._resultNodeValidator
			.initialize();

		return this._resultNodeValidator;
	}

	public clone(): ICollectionValidatorBuilder<TCollectionItem, TCollection> {
		return new CollectionValidatorBuilder<TCollectionItem, TCollection>(this);
	}

	public addValidators(
		...validators: readonly IObjectValidatorBuilder<TCollectionItem>[]
	): CollectionValidatorBuilder<TCollectionItem, TCollection> {
		if (isNotNil(this._resultNodeValidator)) {
			throw new Error("Cannot continue building the validation node, because result was already created.");
		}

		this._additionalBuilders.push(...validators);
		return this;
	}
}

export class ObjectValidatorBuilder<TModel> implements
	IValidationConfiguration<TModel> {
	private readonly _directRules: IValidationRule<TModel>[];
	private readonly _childBuilders: Map<StringKeyOf<TModel>, unknown>;
	private readonly _conditionalBuilders: ConditionalBuilderWrapper<TModel>[];
	private readonly _additionalBuilders: IObjectValidatorBuilder<TModel>[];
	private _resultNodeValidator: InitializableValidator<TModel> | undefined;

	constructor(
		another?: ObjectValidatorBuilder<TModel>
	) {
		if (another) {
			this._directRules = [...another._directRules];
			this._childBuilders = new Map();
			for (const [key, childBuilder] of another._childBuilders.entries()) {
				this._childBuilders
					.set(
						key,
						(childBuilder as IObjectValidatorBuilder<unknown> | ICollectionValidatorBuilder<unknown, unknown[]>)
							.clone()
					);
			}

			this._conditionalBuilders = another._conditionalBuilders.map(
				x => ({
					predicate: x.predicate,
					trueBuilder: x.trueBuilder.clone(),
					falseBuilder: x.falseBuilder?.clone()
				})
			);

			this._additionalBuilders = another._additionalBuilders.map(x => x.clone());
		} else {
			this._directRules = [];
			this._childBuilders = new Map();
			this._conditionalBuilders = [];
			this._additionalBuilders = [];
		}
	}

	public clone(): IValidationConfiguration<TModel> {
		return new ObjectValidatorBuilder(this);
	}

	public forScalar<TProperty extends StringKeyOf<TModel>>(
		property: TProperty
	): IValidatorBuilder<TModel[TProperty]> {
		if (isNotNil(this._resultNodeValidator)) {
			throw new Error("Cannot continue building the validation node, because result was already created.");
		}

		return this.forObject(property);
	}

	public forObject<TProperty extends StringKeyOf<TModel>>(
		property: TProperty,
		externalBuilder?: IObjectValidatorBuilder<TModel[TProperty]>
	): IObjectValidatorBuilder<TModel[TProperty]> {
		if (isNotNil(this._resultNodeValidator)) {
			throw new Error("Cannot continue building the validation node, because result was already created.");
		}

		if (externalBuilder) {
			if (this._childBuilders.has(property)) {
				throw new Error(`Cannot register external builder for ${property} because some inline rules are already defined for it`);
			}

			this._childBuilders.set(property, externalBuilder);
			return externalBuilder;
		}

		if (!this._childBuilders.has(property)) {
			const builder = new ObjectValidatorBuilder<TModel[TProperty]>();
			this._childBuilders.set(property, builder);
		}

		const builder: unknown = this._childBuilders.get(property);
		if (!(builder instanceof ObjectValidatorBuilder)) {
			throw new Error("Expected ObjectValidationBuilder at this point.");
		}

		return builder as unknown as IObjectValidatorBuilder<TModel[TProperty]>;
	}

	public forCollection<TProperty extends StringKeyOf<TModel>>(
		property: TProperty
	): ICollectionValidatorBuilderOptional<TModel[TProperty]> {
		if (isNotNil(this._resultNodeValidator)) {
			throw new Error("Cannot continue building the validation node, because result was already created.");
		}

		if (!this._childBuilders.has(property)) {
			const builder = new CollectionValidatorBuilder();
			this._childBuilders.set(property, builder);
		}

		const builder: unknown = this._childBuilders.get(property);
		if (!(builder instanceof CollectionValidatorBuilder)) {
			throw new Error("Expected CollectionValidationBuilder at this point.");
		}

		return builder as unknown as ICollectionValidatorBuilderOptional<TModel[TProperty]>;
	}

	public add(...validationRules: readonly IValidationRule<TModel>[]): this {
		if (isNotNil(this._resultNodeValidator)) {
			throw new Error("Cannot continue building the validation node, because result was already created.");
		}

		this._directRules.push(...validationRules);

		return this;
	}

	public createNode(): IValidator<TModel> {
		if (this._resultNodeValidator) {
			return this._resultNodeValidator;
		}

		this._resultNodeValidator = new InitializableValidator<TModel>(
			(): NodeValidator<TModel> => {
				const childValidators: IValidator<TModel>[] = [];

				for (const property of this._childBuilders.keys()) {
					const childBuilder = this._childBuilders.get(property) as IValidatorBuilder<TModel[StringKeyOf<TModel>]>;
					const childValidator: IValidator<TModel[StringKeyOf<TModel>]> = childBuilder.createNode() as IValidator<TModel[StringKeyOf<TModel>]>;

					const childPropertyValidator = new PropertyValidator<TModel, StringKeyOf<TModel>>(
						property,
						childValidator
					);

					childValidators.push(childPropertyValidator);
				}

				for (let i = 0; i < this._conditionalBuilders.length; i++) {
					const conditionalBuilderWrapper: ConditionalBuilderWrapper<TModel> = this._conditionalBuilders[i];
					const trueValidator: IValidator<TModel> = conditionalBuilderWrapper.trueBuilder
						.createNode();

					const falseValidator: IValidator<TModel> | undefined = conditionalBuilderWrapper.falseBuilder
						?.createNode();

					const conditionalValidator = new ConditionalValidator<TModel>(
						i,
						conditionalBuilderWrapper.predicate,
						trueValidator,
						falseValidator
					);

					childValidators.push(conditionalValidator);
				}

				for (let i = 0; i < this._additionalBuilders.length; i++) {
					const additionalBuilder: IObjectValidatorBuilder<TModel> = this._additionalBuilders[i];
					const validator: IValidator<TModel> = additionalBuilder.createNode();

					const wrappedValidator = new AdditionalValidator<TModel>(
						validator,
						i
					);

					childValidators.push(wrappedValidator);
				}

				const result = new NodeValidator<TModel>(
					this._directRules,
					childValidators
				);

				return result;
			}
		);

		this._resultNodeValidator
			.initialize();

		return this._resultNodeValidator;
	}

	public whenType<TConditionalModel extends TModel>(
		typeGuardPredicate: (model: TModel) => model is TConditionalModel,
		conditionalBuilder?: IObjectValidatorBuilder<TConditionalModel>
	): IObjectValidatorBuilder<TConditionalModel> {
		if (isNotNil(this._resultNodeValidator)) {
			throw new Error("Cannot continue building the validation node, because result was already created.");
		}

		conditionalBuilder ??= new ObjectValidatorBuilder<TConditionalModel>();

		this._conditionalBuilders.push({
			predicate: typeGuardPredicate,
			trueBuilder: conditionalBuilder as IObjectValidatorBuilder<TModel>,
			falseBuilder: undefined
		});

		return conditionalBuilder;
	}

	public when(
		predicate: (model: TModel) => boolean
	): IObjectValidatorBuilder<TModel>;
	public when(
		predicate: (model: TModel) => boolean,
		trueBuilder: IObjectValidatorBuilder<TModel>
	): IObjectValidatorBuilder<TModel>;
	public when(
		predicate: (model: TModel) => boolean,
		trueBuilder: IObjectValidatorBuilder<TModel>,
		falseBuilder: IObjectValidatorBuilder<TModel>
	): void;
	public when(
		predicate: (model: TModel) => boolean,
		trueBuilder?: IObjectValidatorBuilder<TModel>,
		falseBuilder?: IObjectValidatorBuilder<TModel>
	): IObjectValidatorBuilder<TModel> {
		if (isNotNil(this._resultNodeValidator)) {
			throw new Error("Cannot continue building the validation node, because result was already created.");
		}

		trueBuilder ??= new ObjectValidatorBuilder<TModel>();

		this._conditionalBuilders.push({
			predicate,
			trueBuilder,
			falseBuilder
		});

		return trueBuilder;
	}

	public addValidators(...validators: readonly IObjectValidatorBuilder<TModel>[]) {
		if (isNotNil(this._resultNodeValidator)) {
			throw new Error("Cannot continue building the validation node, because result was already created.");
		}

		validators.forEach(
			x => this._additionalBuilders
				.push(x)
		);
		return this;
	}
}

type ConditionalBuilderWrapper<TModel> = {
	predicate(model: TModel): boolean;
	readonly trueBuilder: IObjectValidatorBuilder<TModel>;
	readonly falseBuilder: IObjectValidatorBuilder<TModel> | undefined;
};

class InitializableValidator<TValue> implements IValidator<TValue> {
	private readonly _validatorFactory: () => IValidator<TValue>;
	private _validator: IValidator<TValue> | undefined;

	constructor(
		validatorFactory: () => IValidator<TValue>
	) {
		this._validatorFactory = validatorFactory;
	}

	public initialize(): void {
		if (this._validator) {
			throw new Error("Validator is already initialized.");
		}

		this._validator = this._validatorFactory();
	}

	public execute(
		currentScope: ValidatorExecutionScope<TValue>,
		params: ExecuteValidationParams,
		nodeStates: readonly NodeValidationState[],
		conversionErrorMap: ReadonlyMap<string, ConversionError>,
		signal?: AbortSignal
	): Promise<readonly NodeValidationState[]> {
		if (!this._validator) {
			throw new Error("Validator was not initialized.");
		}

		return this._validator
			.execute(
				currentScope,
				params,
				nodeStates,
				conversionErrorMap,
				signal
			);
	}

	public getMatchingStablePaths(
		currentScope: ValidatorExecutionScope<TValue>,
		pathToFind: ModelPath
	): readonly string[] {
		if (!this._validator) {
			throw new Error("Validator was not initialized.");
		}

		return this._validator.getMatchingStablePaths(
			currentScope,
			pathToFind
		);
	}
}